// Import Dependencies
import {
  UserPlusIcon,
  PhoneIcon,
  EnvelopeIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  UserIcon,
  CogIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  BellAlertIcon,
  PaperAirplaneIcon,
  VideoCameraIcon,
  PencilSquareIcon,
  ShareIcon,
  FlagIcon,
  XCircleIcon,
  InboxIcon,
} from "@heroicons/react/24/outline";
import { DocumentArrowDownIcon } from "@heroicons/react/24/outline";
import { ArrowUpRightIcon } from "@heroicons/react/20/solid";

// Third-party Imports
import InfiniteScroll from "react-infinite-scroll-component";

// Local Imports
import { Timeline, Tag, Button, TimelineItem } from "components/ui";
import { Card } from "components/ui";
import { getLeadTimeline } from "services/leadsService";
import { formatDisplayDate } from "../../../utils/dateUtils";
import { useState, useEffect, useCallback, useRef } from "react";

// ----------------------------------------------------------------------

function LeadTimeline({ leadId, entityName = "Lead", scrollTargetId = null, isActive = true }) {
  // State management
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [error, setError] = useState(null);

  // Refs
  const timelineScrollElementId = `timeline-scroll-${leadId}`;
  const isFetchingRef = useRef(false);
  const scrollRef = useRef(null);
  const nextPageRef = useRef(1);
  const activitiesCountRef = useRef(0); // Track count via ref
  const hasMoreRef = useRef(true);

  const PAGE_SIZE = 20; // Number of activities per API call

  // Function to get activity icon and color based on type and details
  const getActivityStyle = (activityType, details = "") => {
    // Handle undefined or null activityType
    if (!activityType) {
      return { icon: ClockIcon, color: "neutral", pointClass: "this:neutral" };
    }

    let type = activityType.toLowerCase();
    const detailsLower = (details || "").toLowerCase();

    // If generic "Activity", try to infer type from details for better styling
    if (type === "activity") {
      if (
        detailsLower.includes("follow-up") ||
        detailsLower.includes("follow up")
      ) {
        type = "follow-up";
      } else if (
        detailsLower.includes("call") ||
        detailsLower.includes("phone")
      ) {
        type = "call";
      } else if (
        detailsLower.includes("meeting") ||
        detailsLower.includes("schedule")
      ) {
        type = "meeting";
      } else if (detailsLower.includes("email") || detailsLower.includes("mail")) {
        type = "email";
      }
    }

    // Stage Change activities with specific icons based on details
    if (type.includes("stage")) {
      if (detailsLower.includes("interested")) {
        return {
          icon: CheckCircleIcon,
          color: "success",
          pointClass: "this:success",
        };
      }
      if (
        detailsLower.includes("enquiry") ||
        detailsLower.includes("inquiry")
      ) {
        return {
          icon: InformationCircleIcon,
          color: "info",
          pointClass: "this:info",
        };
      }
      if (detailsLower.includes("closed") || detailsLower.includes("won")) {
        return { icon: FlagIcon, color: "success", pointClass: "this:success" };
      }
      if (detailsLower.includes("lost") || detailsLower.includes("rejected")) {
        return { icon: XCircleIcon, color: "error", pointClass: "this:error" };
      }
      if (detailsLower.includes("proposal") || detailsLower.includes("quote")) {
        return {
          icon: DocumentTextIcon,
          color: "warning",
          pointClass: "this:warning",
        };
      }
      if (
        detailsLower.includes("negotiation") ||
        detailsLower.includes("discussion")
      ) {
        return {
          icon: ChatBubbleLeftRightIcon,
          color: "warning",
          pointClass: "this:warning",
        };
      }
      // Default stage change
      return { icon: ArrowPathIcon, color: "info", pointClass: "this:info" };
    }

    // Communication activities
    if (type.includes("email") || type.includes("mail")) {
      return {
        icon: EnvelopeIcon,
        color: "primary",
        pointClass: "this:primary",
      };
    }

    if (type.includes("call") || type.includes("phone")) {
      return {
        icon: PhoneIcon,
        color: "secondary",
        pointClass: "this:secondary",
      };
    }

    if (
      type.includes("message") ||
      type.includes("sms") ||
      type.includes("text")
    ) {
      return {
        icon: PaperAirplaneIcon,
        color: "info",
        pointClass: "this:info",
      };
    }

    if (type.includes("chat") || type.includes("whatsapp")) {
      return {
        icon: ChatBubbleLeftRightIcon,
        color: "success",
        pointClass: "this:success",
      };
    }

    if (
      type.includes("video") ||
      type.includes("meeting") ||
      type.includes("demo")
    ) {
      return {
        icon: VideoCameraIcon,
        color: "warning",
        pointClass: "this:warning",
      };
    }

    // Document and task activities
    if (
      type.includes("document") ||
      type.includes("file") ||
      type.includes("upload")
    ) {
      return {
        icon: DocumentArrowDownIcon,
        color: "info",
        pointClass: "this:info",
      };
    }

    if (
      type.includes("note") ||
      type.includes("comment") ||
      type.includes("remark")
    ) {
      return {
        icon: PencilSquareIcon,
        color: "neutral",
        pointClass: "this:neutral",
      };
    }

    if (type.includes("task") || type.includes("assignment")) {
      return {
        icon: CheckCircleIcon,
        color: "warning",
        pointClass: "this:warning",
      };
    }

    if (type.includes("follow") || type.includes("reminder")) {
      return {
        icon: BellAlertIcon,
        color: "warning",
        pointClass: "this:warning",
      };
    }

    if (type.includes("share") || type.includes("forward")) {
      return { icon: ShareIcon, color: "info", pointClass: "this:info" };
    }

    // System activities
    if (
      type.includes("created") ||
      type.includes("added") ||
      type.includes("new")
    ) {
      return {
        icon: UserPlusIcon,
        color: "success",
        pointClass: "this:success",
      };
    }

    if (
      type.includes("updated") ||
      type.includes("modified") ||
      type.includes("changed")
    ) {
      return { icon: CogIcon, color: "info", pointClass: "this:info" };
    }

    if (type.includes("deleted") || type.includes("removed")) {
      return { icon: XCircleIcon, color: "error", pointClass: "this:error" };
    }

    if (
      type.includes("alert") ||
      type.includes("warning") ||
      type.includes("issue")
    ) {
      return {
        icon: ExclamationTriangleIcon,
        color: "error",
        pointClass: "this:error",
      };
    }

    if (
      type.includes("schedule") ||
      type.includes("calendar") ||
      type.includes("appointment")
    ) {
      return { icon: CalendarDaysIcon, color: "info", pointClass: "this:info" };
    }

    // Default fallback
    return { icon: ClockIcon, color: "neutral", pointClass: "this:neutral" };
  };

  // Helper function to infer proper title from activity
  const getActivityTitle = (activity) => {
    let type = activity.ActivityType || "Activity";
    const detailsLower = (activity.Details || "").toLowerCase();

    // If generic "Activity", try to infer type from details
    if (type === "Activity") {
      if (
        detailsLower.includes("follow-up") ||
        detailsLower.includes("follow up")
      ) {
        return "Follow-up Activity";
      }
      if (detailsLower.includes("call") || detailsLower.includes("phone")) {
        return "Call Activity";
      }
      if (
        detailsLower.includes("meeting") ||
        detailsLower.includes("schedule")
      ) {
        return "Scheduled Event";
      }
      if (detailsLower.includes("email")) {
        return "Email Activity";
      }
    }

    return type;
  };



  // Function to extract useful info from activity details
  const formatActivityDetails = (details) => {
    if (!details) return { main: "No details available", remarks: null };

    // Common pattern in details: "Some action. Remarks: some remarks"
    const parts = details.split(/Remarks:|Note:/i);
    return {
      main: parts[0].trim(),
      remarks: parts.length > 1 ? parts[1].trim() : null,
    };
  };

  // Helper to get stage change tag
  const getStageTag = (details, type) => {
    if (!type?.toLowerCase().includes("stage")) return null;

    // Try to extract stage names if available in details
    if (details?.includes(" to ")) {
      const parts = details.split(" to ");
      const toStage = parts[1]?.split(".")[0]?.trim();
      if (toStage) {
        return { label: toStage, color: "info" };
      }
    }

    return null;
  };

  // Fetch timeline data from API
  // Fetch timeline data from API - Fixed dependencies
  const fetchTimelineData = useCallback(
    async (page, isLoadMore, forceRefresh = false) => {
      if (isFetchingRef.current) return;
      if (isLoadMore && !hasMoreRef.current) return;
      // if (loading || isFetchingRef.current) return;
      // if (isLoadMore && !hasMore) return;

      // Prevent duplicate fetches
      isFetchingRef.current = true;
      setLoading(true);
      setError(null);

      try {
        const result = await getLeadTimeline(leadId, page, PAGE_SIZE, forceRefresh);

        if (result.success) {
          const newActivities = result.data || [];
          const total = parseInt(result.totalRecords || 0, 10);
          const currentNewCount = newActivities.length;

          setTotalRecords(prev => Math.max(total, prev, currentNewCount));

          if (isLoadMore) {
            setActivities((prev) => {
              const updated = [...prev, ...newActivities];
              activitiesCountRef.current = updated.length;
              return updated;
            });
          } else {
            setActivities(newActivities);
            activitiesCountRef.current = newActivities.length;
          }

          // Check if we have more data
          const currentTotalCount = isLoadMore
            ? activitiesCountRef.current
            : newActivities.length;

          // Robust hasMore check
          const shouldHaveMore = currentTotalCount < total && currentNewCount > 0;
          setHasMore(shouldHaveMore);

          // Update next page ref only if we got data and there's more to fetch
          if (currentNewCount > 0 && shouldHaveMore) {
            nextPageRef.current = page + 1;
          } else {
            setHasMore(false);
          }
        } else {
          setError(result.message || "Failed to load timeline");
          setHasMore(false);
        }
      } catch (err) {
        setError(err.message || "An error occurred");
        setHasMore(false);
        console.error("Timeline fetch error:", err);
      } finally {
        setLoading(false);
        isFetchingRef.current = false;
      }
    },
    [leadId] // Only leadId as dependency
  );

  // Initial data fetch or tab revisit
  const lastLeadIdRef = useRef(leadId);
  useEffect(() => {
    if (leadId && isActive) {
      const isNewLead = leadId !== lastLeadIdRef.current;
      lastLeadIdRef.current = leadId;

      if (isNewLead || activitiesCountRef.current === 0) {
        // Reset all state
        setActivities([]);
        setHasMore(true);
        setTotalRecords(0);
        setError(null);
        nextPageRef.current = 1;
        activitiesCountRef.current = 0;
        isFetchingRef.current = false;
  
        fetchTimelineData(1, false, true); // Always force fresh data on first visit
      } else {
        // Background refresh without clearing existing data to prevent UI flash
        nextPageRef.current = 1;
        isFetchingRef.current = false;
        fetchTimelineData(1, false, true);
      }
    }
  }, [leadId, fetchTimelineData, isActive]);
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);


  // Load more data function - No dependencies on activities
  const loadMoreData = useCallback(() => {   
    if (!hasMoreRef.current || isFetchingRef.current) return;
    fetchTimelineData(nextPageRef.current, true);
  }, [hasMoreRef, fetchTimelineData]);

  // Manual refresh function
  const handleRefresh = () => {
    setActivities([]);
    setHasMore(true);
    setTotalRecords(0);
    setError(null);
    nextPageRef.current = 1;
    activitiesCountRef.current = 0;
    isFetchingRef.current = false;
    fetchTimelineData(1, false, true); // ✅ Pass true for forceRefresh
  };

  return (
    <Card className="flex h-full flex-col border-none bg-transparent shadow-none overflow-hidden">
      {/* Fixed Header */}
      <div className="px-6 py-4 flex items-center justify-between bg-white/50 dark:bg-dark-900/50 backdrop-blur-sm border-b border-gray-100 dark:border-dark-800 shrink-0">
        <h2 className="dark:text-dark-100 truncate font-medium tracking-wide text-gray-800">
          Timeline
          {totalRecords > 0 && (
            <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
              ({totalRecords} activities)
            </span>
          )}
          {/* Loading indicator in header */}
          {loading && (
            <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-xs font-medium">
              <div className="border-blue-500 h-2 w-2 animate-spin rounded-full border-2 border-t-transparent mr-1"></div>
              Loading...
            </span>
          )}
        </h2>
        <div className="flex items-center gap-2">
          {/* Refresh button */}
          <Button
            variant="flat"
            size="sm"
            onClick={handleRefresh}
            disabled={loading}
            className="h-9 w-9 p-0 ml-2 rounded-xl bg-gray-100/50 dark:bg-dark-800/50 hover:bg-primary-50 dark:hover:bg-primary-950/30 transition-all"
          >
            <ArrowPathIcon
              className={`h-4.5 w-4.5 text-gray-600 dark:text-dark-300 ${loading ? "animate-spin text-primary-500" : ""}`}
            />
          </Button>

          {activities.length > 0 && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Loaded: {activities.length}
            </span>
          )}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mx-4 mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Scrollable Content */}
      <div
        id={timelineScrollElementId}
        ref={scrollRef}
        className={`flex-1 min-h-0 overflow-x-hidden custom-scrollbar px-0 py-4 ${!scrollTargetId ? 'overflow-y-auto' : ''}`}
      >
        {activities.length > 0 ? (
          <InfiniteScroll
            dataLength={activities.length}
            next={loadMoreData}
            hasMore={hasMore && !loading}
            loader={loading ? (
              <div className="flex justify-center py-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="border-primary-500 h-5 w-5 animate-spin rounded-full border-2 border-t-transparent"></div>
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      <div className="font-medium">Loading more activities...</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Please wait while we fetch more data
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
            endMessage={
              <div className="py-6 text-center">
                <div className="inline-flex items-center gap-2 rounded-xl bg-gray-50 px-4 py-2 text-gray-600 shadow-sm dark:bg-gray-800 dark:text-gray-300">
                  <CheckCircleIcon className="h-4 w-4 text-green-500" />
                  <span className="text-sm font-medium">
                    No more activities to load
                  </span>
                </div>
              </div>
            }
            scrollableTarget={scrollTargetId || timelineScrollElementId}
            className="min-h-full"
          >
            <Timeline pointSize="1.5rem">
              {activities.map((activity, index) => {
                const {
                  icon: IconComponent,
                  color,
                  pointClass,
                } = getActivityStyle(activity.ActivityType, activity.Details);
                const { main, remarks } = formatActivityDetails(
                  activity.Details,
                );
                const stageTag = getStageTag(
                  activity.Details,
                  activity.ActivityType,
                );

                return (
                  <TimelineItem
                    key={activity.ActivityId || `${activity.ActivityType || 'activity'}-${index}`}
                    title={getActivityTitle(activity)}
                    time={activity.ActivityDate}
                    point={
                      <div
                        className={`timeline-item-point ${pointClass} text-this dark:text-this-light relative flex shrink-0 items-center justify-center rounded-xl border-2 border-current shadow-lg shadow-current/10 bg-white dark:bg-dark-800`}
                      >
                        <IconComponent className="text-[10px] stroke-[2.5px]" />
                      </div>
                    }
                  >
                    <p className="break-all whitespace-pre-wrap">{main}</p>

                    {remarks && (
                      <p className="mt-1 text-sm text-gray-600 italic dark:text-gray-400 break-all whitespace-pre-wrap">
                        Note: {remarks}
                      </p>
                    )}

                    {stageTag && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        <Tag
                          variant="soft"
                          color={stageTag.color}
                          className="border-this-darker/40 dark:border-this-lighter/30 rounded-full border"
                        >
                          {stageTag.label}
                        </Tag>
                      </div>
                    )}

                    <div className="mt-3 space-y-2">
                      {(activity.DoneByName || activity.DoneBy) && (
                        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                          <UserIcon className="h-3 w-3" />
                          <span>By {activity.DoneByName || activity.DoneBy}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <CalendarDaysIcon className="h-3 w-3" />
                        <span>
                          {formatDisplayDate(activity.ActivityDate)}
                        </span>
                      </div>
                    </div>
                  </TimelineItem>
                );
              })}
            </Timeline>
          </InfiniteScroll>
        ) : (
          !loading && (
            <div className="flex h-full items-center justify-center">
              <div className="text-center">
                <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                  No Activities
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  No activities found for this lead yet.
                </p>
              </div>
            </div>
          )
        )}
      </div>
    </Card>
  );
}

export default LeadTimeline;